import {
  AutomationActionStatus,
  getSubTaskTemplates,
  IAutomation,
  IAutomationTemplate,
  ITask,
  ITaskTemplate,
  SubTaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { getItemDisplayName } from '../../../../features/Experiences/helpers/adminName';
import { interpolateExperienceText } from '../../../../services/set/interpolation';
import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { compact, keyBy, mapValues } from 'lodash';
import { Calendar, Zap } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CompletedLabel,
  ErroredLabel,
  SkippedLabel
} from '../../../../components/CompletionButtons';
import { SubTaskAsText } from '../../../../components/SubTask';
import { SubTaskIcon } from '../../../../components/SubTaskIcon';
import { WeeksAndDaysDuration } from '../../../../components/WeeksAndDaysDuration';
import { useUnsafeTranslation } from '../../../../i18next';
import { ONE_DAY } from '../../../../services/time';
import { useActionDefinitions } from '../../../Experiences/components/Builders/AutomationBuilder/actionHelpers';

const Body = styled('div')((p) => ({
  padding: p.theme.spacing(2),
  minWidth: 320
}));

const Heading = styled('div')((p) => ({
  fontSize: p.theme.custom.fontSize.s,
  color: p.theme.custom.colors.gray.w500,
  textTransform: 'uppercase',
  marginBottom: p.theme.spacing()
}));

const Title = styled('div')((p) => ({
  fontSize: p.theme.custom.fontSize.l,
  fontWeight: p.theme.typography.fontWeightBold,
  marginBottom: p.theme.spacing()
}));

const Row = styled('div')((p) => ({
  color: p.theme.custom.colors.gray.w500,
  marginBottom: p.theme.spacing(),
  display: 'flex',
  alignItems: 'center',
  gap: p.theme.spacing()
}));

const ICON_SIZE = 16;

const TaskCompletionCriteria = ({
  criteria,
  totalSubTaskCount
}: {
  criteria: ITaskTemplate['customCompletionCriteria'];
  totalSubTaskCount: number;
}) => {
  const { t } = useTranslation();
  if (!criteria.length) {
    return null;
  }
  if (criteria.length > 1) {
    console.log(
      'Several completion criteria found, but only supporting one at the moment',
      criteria
    );
  }
  const criterium = criteria[0];
  if (criterium.type !== 'SUBSET') {
    console.log('Criterium not supported yet', criterium);
    return null;
  }

  return (
    <Row>
      <Typography variant="caption">
        {t('taskCompletionCriteria.adminFacing.hint', {
          requiredCount: criterium.minimumRequired,
          count: totalSubTaskCount
        })}
      </Typography>
    </Row>
  );
};
const IconContainer = styled('div')((p) => ({
  minWidth: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

export const TaskPopoverBody = ({
  tpl,
  task,
  expTitle,
  orgName
}: {
  tpl: ITaskTemplate;
  task: ITask;
  expTitle: string;
  orgName: string;
}) => {
  const { t } = useTranslation();
  const duration = task.timing.end.toMillis() - task.timing.start.toMillis();
  const displayTitle = interpolateExperienceText(
    getItemDisplayName(tpl),
    {
      experience: { vars: { title: expTitle, tasks: {} } },
      org: { vars: { name: orgName } },
      roles: []
    },
    { dateFormat: 'YYYY/MM/DD' }
  );
  const subTasks = useMemo(() => {
    const statuses = mapValues(
      keyBy(task.subTasks, (s) => s.templateId),
      (s) => s.status
    );
    return compact(
      getSubTaskTemplates(tpl).map((s) => {
        const status = statuses[s.id];
        if (!status) {
          return null;
        }
        return {
          s,
          status
        };
      })
    );
  }, [tpl, task]);
  return (
    <Body>
      <Heading>{t('Task')}</Heading>
      <Title>{displayTitle}</Title>
      <Row>
        <Calendar size={ICON_SIZE} />
        {duration !== null && (
          <div>
            <WeeksAndDaysDuration days={Math.ceil(duration / ONE_DAY)} />
          </div>
        )}
      </Row>
      <TaskCompletionCriteria
        criteria={tpl.customCompletionCriteria}
        totalSubTaskCount={subTasks.length}
      />
      {subTasks.map((s) => (
        <Row key={s.s.id}>
          <IconContainer>
            <SubTaskIcon key={s.s.id} s={s.s} />
          </IconContainer>
          <div>
            <SubTaskAsText key={s.s.id} s={s.s} />
          </div>

          {s.status === SubTaskStatus.DONE && <CompletedLabel />}
        </Row>
      ))}
    </Body>
  );
};

export const AutomationPopoverBody = ({
  tpl,
  automation
}: {
  tpl: IAutomationTemplate;
  automation: IAutomation;
}) => {
  const { t } = useUnsafeTranslation();
  const defs = useActionDefinitions();
  const rows: {
    id: string;
    label: React.ReactNode;
    status: AutomationActionStatus;
  }[] = useMemo(() => {
    return Object.entries(automation.actions).map(([id, a]) => {
      const def = defs.find((d) => d.type === a.type);
      const label = def?.label || t('Unknown action type');
      return { id, label, status: a.status };
    });
  }, [automation, defs, t]);

  console.log(rows, automation);
  return (
    <Body>
      <Heading>{t('Automation')}</Heading>
      <Title>{tpl.title}</Title>
      {rows.map((r) => (
        <Row key={r.id}>
          <Zap size={ICON_SIZE} />
          <div>{r.label}</div>
          {r.status === AutomationActionStatus.DONE && <CompletedLabel />}
          {r.status === AutomationActionStatus.ERROR && <ErroredLabel />}
          {r.status === AutomationActionStatus.SKIPPED && <SkippedLabel />}
        </Row>
      ))}
    </Body>
  );
};
